import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StatusBadge } from '@/components/StatusBadge';
import { Eye, RotateCcw, Plus, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function Revision() {
  const [tab, setTab] = useState('salida');
  const [warehouses, setWarehouses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [disponible, setDisponible] = useState([]);
  const [saving, setSaving] = useState(false);

  // Salida form (header + lines)
  const [sWarehouse, setSWarehouse] = useState('');
  const [sResponsable, setSResponsable] = useState('');
  const [sObs, setSObs] = useState('');
  const [sLineas, setSLineas] = useState([{ group_code: '', cantidad: '' }]);

  // Devolucion
  const [dFolio, setDFolio] = useState('');
  const [dObs, setDObs] = useState('');
  const [dLineas, setDLineas] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);

  const fetchAll = async () => {
    try {
      const [w, g, r, p] = await Promise.all([
        API.get('/catalogs/warehouses'), API.get('/catalogs/groups'),
        API.get('/revision'), API.get('/revision/pendientes')
      ]);
      setWarehouses(w.data); setGroups(g.data); setReviews(r.data); setPendientes(p.data);
    } catch { toast.error('Error cargando datos'); }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (sWarehouse) {
      API.get('/revision/disponible', { params: { warehouse_id: sWarehouse } }).then(res => setDisponible(res.data));
    }
  }, [sWarehouse]);

  // Load review for devolucion
  useEffect(() => {
    if (dFolio) {
      const rev = pendientes.find(r => r.folio_revision === dFolio);
      setSelectedReview(rev);
      if (rev) {
        setDLineas(rev.lineas.filter(l => l.cantidad_pendiente > 0).map(l => ({
          group_code: l.group_code, cantidad_pendiente: l.cantidad_pendiente,
          cantidad_salida: l.cantidad_salida, cantidad_regresada: l.cantidad_regresada,
          cantidad_devuelta: ''
        })));
      }
    } else {
      setSelectedReview(null); setDLineas([]);
    }
  }, [dFolio, pendientes]);

  const addSLine = () => setSLineas([...sLineas, { group_code: '', cantidad: '' }]);
  const removeSLine = (idx) => setSLineas(sLineas.filter((_, i) => i !== idx));
  const updateSLine = (idx, field, value) => {
    const updated = [...sLineas]; updated[idx][field] = value; setSLineas(updated);
  };

  const totalSalida = sLineas.reduce((s, l) => s + (parseInt(l.cantidad) || 0), 0);

  const handleSalida = async () => {
    if (!sWarehouse || !sResponsable) { toast.error('Selecciona almacén y responsable'); return; }
    const validLines = sLineas.filter(l => l.group_code && parseInt(l.cantidad) > 0);
    if (validLines.length === 0) { toast.error('Agrega al menos una línea'); return; }
    setSaving(true);
    try {
      const res = await API.post('/revision/salida', {
        warehouse_id: sWarehouse, responsable: sResponsable, observaciones: sObs,
        lineas: validLines.map(l => ({ group_code: l.group_code, cantidad: parseInt(l.cantidad) }))
      });
      toast.success(`Salida registrada: ${res.data.folio_revision} (${res.data.total_salida} pzas)`);
      setSLineas([{ group_code: '', cantidad: '' }]); setSResponsable(''); setSObs('');
      fetchAll();
      if (sWarehouse) API.get('/revision/disponible', { params: { warehouse_id: sWarehouse } }).then(r => setDisponible(r.data));
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  const handleDevolucion = async () => {
    if (!dFolio) { toast.error('Selecciona un folio'); return; }
    const validLines = dLineas.filter(l => parseInt(l.cantidad_devuelta) > 0);
    if (validLines.length === 0) { toast.error('Ingresa cantidad a devolver en al menos una línea'); return; }
    setSaving(true);
    try {
      const res = await API.post('/revision/devolucion', {
        folio_revision: dFolio, observaciones: dObs,
        lineas: validLines.map(l => ({ group_code: l.group_code, cantidad_devuelta: parseInt(l.cantidad_devuelta) }))
      });
      toast.success(`Devolución registrada. Pendiente total: ${res.data.total_pendiente}`);
      setDFolio(''); setDObs(''); setDLineas([]);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Revisión de Acumuladores</h1>
        <p className="text-sm text-muted-foreground">Salidas temporales para revisión y devoluciones (formato multi-grupo)</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="salida">Salida a Revisión</TabsTrigger>
          <TabsTrigger value="devolucion">Devolución</TabsTrigger>
          <TabsTrigger value="pendientes" className="relative">
            Pendientes
            {pendientes.length > 0 && <span className="ml-1 h-5 w-5 rounded-full bg-amber-500 text-white text-[10px] inline-flex items-center justify-center">{pendientes.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="historial">Historial</TabsTrigger>
        </TabsList>

        {/* === SALIDA (vale-style) === */}
        <TabsContent value="salida" className="mt-4 space-y-4">
          <Card className="bg-white border">
            <CardHeader className="pb-3"><CardTitle className="text-base">Datos de Salida</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium">Almacén *</label>
                  <Select value={sWarehouse} onValueChange={setSWarehouse}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Responsable *</label>
                  <Input value={sResponsable} onChange={(e) => setSResponsable(e.target.value)} placeholder="Ej: Rubén" className="mt-1" />
                </div>
                <div>
                  <label className="text-sm font-medium">Observaciones</label>
                  <Input value={sObs} onChange={(e) => setSObs(e.target.value)} placeholder="Opcional" className="mt-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Disponible quick view */}
          {disponible.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {disponible.map(d => (
                <span key={d.group_code} className="text-xs px-2.5 py-1 rounded-full border bg-white">
                  {d.group_code}: <strong>{d.disponible}</strong> pzas
                </span>
              ))}
            </div>
          )}

          <Card className="bg-white border">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Detalle de Acumuladores</CardTitle>
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold">Total: {totalSalida} pzas</span>
                <Button size="sm" variant="outline" onClick={addSLine}><Plus className="h-3.5 w-3.5 mr-1" /> Línea</Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-muted/30">
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>Grupo *</TableHead>
                  <TableHead>Cantidad *</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {sLineas.map((line, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell>
                        <Select value={line.group_code} onValueChange={(v) => updateSLine(idx, 'group_code', v)}>
                          <SelectTrigger><SelectValue placeholder="Grupo" /></SelectTrigger>
                          <SelectContent>{groups.map(g => <SelectItem key={g.codigo} value={g.codigo}>{g.codigo}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input type="number" min="1" value={line.cantidad} onChange={(e) => updateSLine(idx, 'cantidad', e.target.value)} placeholder="0" />
                      </TableCell>
                      <TableCell>
                        {sLineas.length > 1 && <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeSLine(idx)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button onClick={handleSalida} disabled={saving} className="bg-[#0F6E87] hover:bg-[#0C5D73]">
              <Send className="h-4 w-4 mr-1" /> {saving ? 'Registrando...' : 'Registrar Salida'}
            </Button>
          </div>
        </TabsContent>

        {/* === DEVOLUCIÓN === */}
        <TabsContent value="devolucion" className="mt-4 space-y-4">
          <Card className="bg-white border">
            <CardHeader className="pb-3"><CardTitle className="text-base">Devolución de Revisión</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Folio de Revisión</label>
                  <Select value={dFolio} onValueChange={setDFolio}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{pendientes.map(r => <SelectItem key={r.folio_revision} value={r.folio_revision}>{r.folio_revision} — {r.responsable} (pendiente: {r.total_pendiente})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Observaciones</label>
                  <Input value={dObs} onChange={(e) => setDObs(e.target.value)} placeholder="Opcional" className="mt-1" />
                </div>
              </div>

              {selectedReview && (
                <div className="p-3 rounded-lg bg-muted/50 text-sm">
                  <p>Responsable: <strong>{selectedReview.responsable}</strong> | Total salió: <strong>{selectedReview.total_salida}</strong> | Regresado: <strong>{selectedReview.total_regresado}</strong> | Pendiente: <strong className="text-amber-600">{selectedReview.total_pendiente}</strong></p>
                </div>
              )}

              {dLineas.length > 0 && (
                <Table>
                  <TableHeader><TableRow className="bg-muted/30">
                    <TableHead>Grupo</TableHead>
                    <TableHead className="text-right">Salió</TableHead>
                    <TableHead className="text-right">Regresado</TableHead>
                    <TableHead className="text-right">Pendiente</TableHead>
                    <TableHead>Devolver ahora</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {dLineas.map((l, idx) => (
                      <TableRow key={l.group_code}>
                        <TableCell className="font-medium">{l.group_code}</TableCell>
                        <TableCell className="text-right">{l.cantidad_salida}</TableCell>
                        <TableCell className="text-right">{l.cantidad_regresada}</TableCell>
                        <TableCell className="text-right font-semibold text-amber-600">{l.cantidad_pendiente}</TableCell>
                        <TableCell>
                          <Input type="number" min="0" max={l.cantidad_pendiente}
                            value={l.cantidad_devuelta}
                            onChange={(e) => {
                              const updated = [...dLineas];
                              updated[idx].cantidad_devuelta = e.target.value;
                              setDLineas(updated);
                            }}
                            placeholder="0" className="w-24" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Button onClick={handleDevolucion} disabled={saving || !dFolio} className="bg-[#0F6E87] hover:bg-[#0C5D73]">
                <RotateCcw className="h-4 w-4 mr-1" /> {saving ? 'Registrando...' : 'Registrar Devolución'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === PENDIENTES === */}
        <TabsContent value="pendientes" className="mt-4">
          <Card className="bg-white border"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/30">
                <TableHead>Folio</TableHead><TableHead>Responsable</TableHead><TableHead>Grupos</TableHead>
                <TableHead className="text-right">Total Salió</TableHead><TableHead className="text-right">Regresado</TableHead>
                <TableHead className="text-right">Pendiente</TableHead><TableHead>Estado</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {pendientes.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin revisiones pendientes</TableCell></TableRow>}
                {pendientes.map(r => (
                  <TableRow key={r.folio_revision} className="hover:bg-muted/40">
                    <TableCell className="font-mono text-xs">{r.folio_revision}</TableCell>
                    <TableCell>{r.responsable}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.lineas?.map(l => (
                          <span key={l.group_code} className="text-[10px] px-1.5 py-0.5 rounded bg-muted">
                            {l.group_code}: {l.cantidad_pendiente}/{l.cantidad_salida}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{r.total_salida}</TableCell>
                    <TableCell className="text-right">{r.total_regresado}</TableCell>
                    <TableCell className="text-right font-semibold text-amber-600">{r.total_pendiente}</TableCell>
                    <TableCell><StatusBadge status={r.estatus} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* === HISTORIAL === */}
        <TabsContent value="historial" className="mt-4">
          <Card className="bg-white border"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/30">
                <TableHead>Folio</TableHead><TableHead>Responsable</TableHead><TableHead>Grupos</TableHead>
                <TableHead className="text-right">Salió</TableHead><TableHead className="text-right">Regresado</TableHead>
                <TableHead className="text-right">Pendiente</TableHead><TableHead>Estado</TableHead><TableHead>Fecha</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {reviews.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Sin revisiones</TableCell></TableRow>}
                {reviews.map(r => (
                  <TableRow key={r.folio_revision} className="hover:bg-muted/40">
                    <TableCell className="font-mono text-xs">{r.folio_revision}</TableCell>
                    <TableCell>{r.responsable}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.lineas?.map(l => (
                          <span key={l.group_code} className="text-[10px] px-1.5 py-0.5 rounded bg-muted">{l.group_code}:{l.cantidad_salida}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{r.total_salida}</TableCell>
                    <TableCell className="text-right">{r.total_regresado}</TableCell>
                    <TableCell className="text-right">{r.total_pendiente}</TableCell>
                    <TableCell><StatusBadge status={r.estatus} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.fecha_salida ? new Date(r.fecha_salida).toLocaleDateString('es-MX') : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
